import { Folder, File, FolderUp, Loader2 } from "lucide-react"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip"
import { Info } from "lucide-react"
import LoadingSpinner from "@/components/shared/LoadingSpinner"
import { formatBytes, formatCost } from "./diskUsageUtils"
import type { DiskUsageScope } from "@/services/resourceUsageService"
import { JSX } from "react"

export interface DiskUsageRow {
  name: string
  isDirectory: boolean
  size: number
  loading: boolean
  scope: DiskUsageScope | null
  navigatePath: string | null
}

interface DiskUsageTableBodyProps {
  rows: DiskUsageRow[]
  maxSize: number
  /** When true, shows a full-height initial loading spinner instead of the table. */
  initialLoading: boolean
  navigating: boolean
  isAtRoot: boolean
  costColumnLabel: string
  estimateCost: (bytes: number, scope: DiskUsageScope) => number
  onNavigateUp: () => void
  onRowClick: (row: DiskUsageRow) => void
}

/**
 * DiskUsageTableBody renders the shared table UI for disk usage components.
 *
 * Handles three loading states:
 * - `initialLoading`: shows a full-height spinner before any data is available.
 * - `navigating`: shows a spinner row inside the table while fetching a subdirectory.
 * - `row.loading`: shows an inline spinner in the size cell for rows whose size is still resolving.
 *
 * @param props - See DiskUsageTableBodyProps.
 * @returns A JSX element containing the loading state or the full table.
 */
export default function DiskUsageTableBody({
  rows,
  maxSize,
  initialLoading,
  navigating,
  isAtRoot,
  costColumnLabel,
  estimateCost,
  onNavigateUp,
  onRowClick,
}: DiskUsageTableBodyProps): JSX.Element {
  if (initialLoading) {
    return (
      <div className="flex-1 min-h-0 flex items-center justify-center border border-border/50">
        <LoadingSpinner loadingContent="Loading disk usage data..." />
      </div>
    )
  }

  return (
    <div className="flex-1 min-h-0 overflow-auto border border-border/50">
      <Table>
        <TableHeader className="sticky top-0 z-10 bg-card shadow-sm">
          <TableRow>
            <TableHead className="w-[40%]">Name</TableHead>
            <TableHead className="w-[15%] text-right">Size</TableHead>
            <TableHead className="w-[30%]">Fraction</TableHead>
            <TableHead className="w-[15%] text-right">{costColumnLabel}</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {/* ".." row — allows navigating to the parent level. Hidden at root. */}
          {!isAtRoot && (
            <TableRow className="cursor-pointer hover:bg-muted/50" onClick={onNavigateUp}>
              <TableCell className="font-medium">
                <span className="flex items-center gap-2">
                  <FolderUp className="h-4 w-4 text-muted-foreground" />
                  ..
                </span>
              </TableCell>
              <TableCell className="text-right" />
              <TableCell />
              <TableCell className="text-right" />
            </TableRow>
          )}

          {/* Loading row — shown while a navigation request is in-flight. */}
          {navigating && (
            <TableRow>
              <TableCell colSpan={4} className="text-center py-8">
                <LoadingSpinner loadingContent="Loading..." />
              </TableCell>
            </TableRow>
          )}

          {/* Data rows — one per entry in the sorted rows array. */}
          {!navigating &&
            rows.map((row, index) => {
              // Calculate the bar width as a percentage of the maximum size
              const barWidth = maxSize > 0 ? (row.size / maxSize) * 100 : 0

              return (
                <TableRow
                  key={index}
                  className={row.isDirectory ? "cursor-pointer hover:bg-muted/50" : undefined}
                  onClick={row.isDirectory ? () => onRowClick(row) : undefined}
                >
                  {/* Name column: folder or file icon followed by the entry name */}
                  <TableCell className="font-medium">
                    <span className="flex items-center gap-2">
                      {row.isDirectory ? (
                        <Folder className="h-4 w-4" />
                      ) : (
                        <File className="h-4 w-4" />
                      )}
                      {row.name}
                    </span>
                  </TableCell>

                  {/* Size column: spinner while loading, otherwise formatted byte count */}
                  <TableCell className="text-right font-mono text-sm">
                    {row.loading ? (
                      <Loader2 className="h-3 w-3 animate-spin inline" />
                    ) : (
                      formatBytes(row.size)
                    )}
                  </TableCell>

                  {/* Fraction column: horizontal bar proportional to row.size / maxSize */}
                  <TableCell>
                    {row.loading ? null : (
                      <div className="w-full bg-muted rounded-full h-2.5">
                        <div
                          className="bg-primary h-2.5 rounded-full transition-all duration-300"
                          style={{ width: `${barWidth}%` }}
                        />
                      </div>
                    )}
                  </TableCell>

                  {/* Cost column: formatted cost with an info tooltip for the userdata scope */}
                  <TableCell className="text-right font-mono text-sm">
                    {row.loading ? null : row.scope ? (
                      <span className="inline-flex items-center gap-1 justify-end">
                        {formatCost(estimateCost(row.size, row.scope))}
                        {row.scope === "userdata" && (
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Info className="h-3.5 w-3.5 text-muted-foreground cursor-help" />
                              </TooltipTrigger>
                              <TooltipContent>
                                <p>
                                  Note: this is an estimate based on average usage. <br />
                                  Infrequently used data is automatically moved
                                  <br />
                                  to a much cheaper tier after 30 days of no reading/writing.
                                </p>
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        )}
                      </span>
                    ) : null}
                  </TableCell>
                </TableRow>
              )
            })}

          {/* Empty state — shown when navigation is complete but there are no rows */}
          {!navigating && rows.length === 0 && (
            <TableRow>
              <TableCell colSpan={4} className="text-center py-8 text-muted-foreground">
                No items found.
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </div>
  )
}
